import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    Link,
    Menu,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, Navigate, useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import Loading from '../../components/Loading';
import MoneyInputWithBlur from '../../components/MoneyInputWithBlur';
import PhoneInput from '../../components/PhoneInput';
import { useFeatureFlag } from '../../helpers/featureFlags';
import { formatCurrencyInCents, parseFormattedMoneyToCents } from '../../helpers/formatters';
import { isVisitManagedPartnerModality, partnerModalityLabel } from './partnerLabels';

const emptyItem = () => ({
    product_uuid: '',
    product_name: '',
    item_kind: 'stock_product',
    min_initial_qty: 0,
    min_reorder_qty: 0,
    display_qty: 0,
    wholesale_cents: 0,
    suggested_retail_cents: 0,
    consignment_repasse_cents: 0,
});

const emptyForm = {
    name: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    modality: 'pay_to_sell',
    status: 'active',
    address: '',
    address_number: '',
    address_complement: '',
    neighborhood: '',
    zip_code: '',
    city: '',
    state: '',
    visit_frequency: 'weekly',
    first_visit_on: '',
    notes: '',
    items: [emptyItem()],
};

const modalityDisclaimer = {
    pay_to_sell: '<strong>Resumo</strong>: O comerciante compra em atacado e vende em seu estabelecimento.<br /><br /><strong>Aquisição</strong>: O comerciante adquire os acessórios para exposição, os produtos ao preço de atacado e os vende. <br /><br /><strong>Reposição de estoque</strong>: A reposição é feita mediante novas aquisições.<br /><br /><strong>Lucro</strong>: Nesta modalidade, o valor para o comerciante tende a ser o <strong>mais vantajoso</strong>.',
    consignment: '<strong>Resumo</strong>: O comerciante vende e te paga o valor que foi negociado para cada item.<br /><br /><strong>Aquisição</strong>: O comerciante recebe um estoque inicial, vende e cobra com seus próprios meios (PIX, dinheiro, cartão), absorvendo taxas de cartão.<br /><br /><strong>Reposição de estoque</strong>: A periodicidade da reposição é combinada e o comerciante <strong>paga o valor negociado</strong> para cada item vendido.<br /><br /><strong>Lucro</strong>: Nesta modalidade o valor para o comerciante tende a ser <strong>menos vantajoso</strong> que na modalidade Pay To Sell.',
    repasse: '<strong>Resumo</strong>: O comerciante vende, você recebe na sua conta e paga ao comerciante a comissão que foi negociada para cada item.<br /><br /><strong>Aquisição</strong>: O comerciante recebe um estoque inicial, vende e o cliente paga pelo QR Code PIX diretamente na <strong>sua conta</strong>, eliminando taxas de cartão/PIX ao comerciante.<br /><br /><strong>Reposição de estoque</strong>: A periodicidade da reposição é combinada e você <strong>repassa</strong> o valor negociado para cada item vendido.<br />Se faz necessário <strong>controle preciso</strong> das vendas por parte do <strong>comerciante</strong>, pois a sua conta pode receber PIX de vários locais diariamente.<br /><br /><strong>Lucro</strong>: Nesta modalidade o valor para o comerciante tende a ser <strong>menos vantajoso</strong> que nas demais modalidades.',
};

function centsToReaisInput(cents) {
    const n = Number(cents) || 0;
    return n ? n / 100 : '';
}

function digitsOnly(value) {
    return String(value || '').replace(/\D/g, '');
}

function normalizeCep(value) {
    return digitsOnly(value).slice(0, 8);
}

function formatCep(value) {
    const cep = normalizeCep(value);
    if (cep.length <= 5) {
        return cep;
    }
    return `${cep.slice(0, 5)}-${cep.slice(5)}`;
}

function validatePartnerForm(form) {
    if (!String(form.contact_name || '').trim()) {
        return 'Informe o nome do responsável.';
    }
    const phone = digitsOnly(form.contact_phone);
    if (!phone) {
        return 'Informe o telefone do responsável.';
    }
    if (phone.length < 10) {
        return 'Informe um telefone válido com DDD.';
    }
    const cep = digitsOnly(form.zip_code);
    if (!cep) {
        return 'Informe o CEP.';
    }
    if (cep.length !== 8) {
        return 'Informe um CEP válido com 8 dígitos.';
    }
    if (!String(form.address || '').trim()) {
        return 'Informe o logradouro.';
    }
    if (!String(form.address_number || '').trim()) {
        return 'Informe o número do endereço.';
    }
    return '';
}

function RepasseExampleBox({ retailCents, repasseCents, modality }) {
    const retail = Math.max(0, Number(retailCents) || 0);
    const informed = Math.max(0, Number(repasseCents) || 0);
    const rest = Math.max(0, retail - informed);
    const isRepasse = modality === 'repasse';
    const myShare = isRepasse ? rest : informed;
    const partnerShare = isRepasse ? informed : rest;
    const hasValues = retail > 0 || informed > 0;

    return (
        <Alert severity="info" variant="outlined" sx={{ mt: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                Como fica a divisão
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
                {isRepasse
                    ? 'Você cobra o varejo do cliente e paga só este desconto ao parceiro.'
                    : 'O parceiro cobra o varejo do cliente e te paga o total menos este desconto.'}
            </Typography>
            {hasValues ? (
                <Typography variant="body2" component="div">
                    Exemplo com os valores deste item:
                    <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
                        <li>Cliente paga no varejo: <strong>{formatCurrencyInCents(retail)}</strong></li>
                        <li>Você fica com: <strong>{formatCurrencyInCents(myShare)}</strong></li>
                        <li>Parceiro fica com: <strong>{formatCurrencyInCents(partnerShare)}</strong></li>
                    </Box>
                </Typography>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    {isRepasse
                        ? 'Preencha o varejo e o desconto do parceiro para ver o exemplo.'
                        : 'Preencha o varejo e o valor que você recebe para ver o exemplo.'}
                </Typography>
            )}
        </Alert>
    );
}

function PartnerFormPage() {
    const { uuid } = useParams();
    const isEdit = Boolean(uuid);
    const navigate = useNavigate();
    const { enabled, loading: flagLoading } = useFeatureFlag('commerce_partners');
    const [form, setForm] = useState(emptyForm);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [productResults, setProductResults] = useState([]);
    const [isLoadingProductSearch, setIsLoadingProductSearch] = useState(false);
    const [anchorElProduct, setAnchorElProduct] = useState(null);
    const [searchingItemIndex, setSearchingItemIndex] = useState(null);
    const productSearchTimer = useRef(null);
    const productSearchRequest = useRef(0);
    const suppressProductSearch = useRef(false);
    const cepDebounceTimer = useRef(null);
    const cepRequestId = useRef(0);
    const addressNumberInputRef = useRef(null);
    const [isLoadingCep, setIsLoadingCep] = useState(false);
    const [cepError, setCepError] = useState('');

    useEffect(() => {
        if (!isEdit || !enabled) return undefined;
        setLoading(true);
        api.get(`/commerce-partners/${uuid}`)
            .then((response) => {
                const data = response?.data?.data || {};
                setForm({
                    ...emptyForm,
                    ...data,
                    first_visit_on: String(data.first_visit_on || '').slice(0, 10),
                    items: (data.items || []).length ? data.items : [emptyItem()],
                });
            })
            .catch(() => Swal.fire('Erro', 'Não foi possível carregar a parceria.', 'error'))
            .finally(() => setLoading(false));
        return undefined;
    }, [isEdit, uuid, enabled]);

    useEffect(() => {
        const cep = normalizeCep(form.zip_code);
        if (!cep || cep.length !== 8) {
            setIsLoadingCep(false);
            setCepError('');
            return undefined;
        }

        setCepError('');
        const requestId = cepRequestId.current + 1;
        cepRequestId.current = requestId;

        if (cepDebounceTimer.current) {
            clearTimeout(cepDebounceTimer.current);
        }

        setIsLoadingCep(true);
        cepDebounceTimer.current = setTimeout(() => {
            fetch(`https://viacep.com.br/ws/${cep}/json/`)
                .then((res) => res.json())
                .then((data) => {
                    if (requestId !== cepRequestId.current) {
                        return;
                    }
                    if (data?.erro) {
                        setCepError('CEP inválido.');
                        return;
                    }
                    setForm((prev) => ({
                        ...prev,
                        address: data?.logradouro || '',
                        neighborhood: data?.bairro || '',
                        city: data?.localidade || '',
                        state: data?.uf || '',
                    }));
                    // Após preencher automaticamente o endereço, direciona para o campo de número.
                    setTimeout(() => {
                        if (requestId !== cepRequestId.current) return;
                        addressNumberInputRef.current?.focus?.();
                    }, 50);
                })
                .catch(() => {
                    if (requestId !== cepRequestId.current) {
                        return;
                    }
                    setCepError('Não foi possível buscar o CEP.');
                })
                .finally(() => {
                    if (requestId !== cepRequestId.current) {
                        return;
                    }
                    setIsLoadingCep(false);
                });
        }, 400);

        return () => {
            if (cepDebounceTimer.current) {
                clearTimeout(cepDebounceTimer.current);
            }
        };
    }, [form.zip_code]);

    if (flagLoading || loading) return <Loading />;
    if (!enabled) return <Navigate to="/admin" replace />;

    const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));
    const setItem = (index, name, value) => {
        setForm((prev) => {
            const items = [...prev.items];
            items[index] = { ...items[index], [name]: value };
            return { ...prev, items };
        });
    };

    const closeProductMenu = () => {
        if (productSearchTimer.current) {
            clearTimeout(productSearchTimer.current);
            productSearchTimer.current = null;
        }
        productSearchRequest.current += 1;
        setAnchorElProduct(null);
        setProductResults([]);
        setSearchingItemIndex(null);
        setIsLoadingProductSearch(false);
    };

    const productSearch = (query, index, anchor) => {
        if (suppressProductSearch.current) {
            return;
        }
        setSearchingItemIndex(index);
        setAnchorElProduct(anchor);
        if (query.length < 3) {
            setProductResults([]);
            return;
        }
        if (productSearchTimer.current) {
            clearTimeout(productSearchTimer.current);
        }
        const requestId = productSearchRequest.current + 1;
        productSearchRequest.current = requestId;
        productSearchTimer.current = setTimeout(() => {
            setIsLoadingProductSearch(true);
            api.get('products', { params: { q: query } })
                .then((response) => {
                    if (requestId !== productSearchRequest.current || suppressProductSearch.current) {
                        return;
                    }
                    const d = response.data;
                    const rows = Array.isArray(d?.products)
                        ? d.products
                        : Array.isArray(d?.data)
                            ? d.data
                            : [];
                    setProductResults(rows);
                })
                .catch(() => {
                    if (requestId !== productSearchRequest.current) {
                        return;
                    }
                    setProductResults([]);
                })
                .finally(() => {
                    if (requestId === productSearchRequest.current) {
                        setIsLoadingProductSearch(false);
                    }
                });
        }, 400);
    };

    const selectProduct = (index, product) => {
        suppressProductSearch.current = true;
        setForm((prev) => {
            const items = [...prev.items];
            const current = { ...items[index] };
            current.product_uuid = product.uuid || product.id || '';
            current.product_name = product.name || '';
            if (!Number(current.wholesale_cents) && product.base_price) {
                current.wholesale_cents = Number(product.base_price) || 0;
            }
            if (!Number(current.suggested_retail_cents) && product.base_price) {
                current.suggested_retail_cents = Number(product.base_price) || 0;
            }
            items[index] = current;
            return { ...prev, items };
        });
        closeProductMenu();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationError = validatePartnerForm(form);
        if (validationError) {
            Swal.fire('Atenção', validationError, 'warning');
            return;
        }
        setSaving(true);
        const selectedItems = form.items.filter((item) => item.product_uuid);
        if (!selectedItems.length) {
            Swal.fire('Atenção', 'Busque e selecione pelo menos um produto da lista.', 'warning');
            setSaving(false);
            return;
        }
        const payload = {
            ...form,
            first_visit_on: String(form.first_visit_on || '').slice(0, 10),
            items: selectedItems.map((item) => ({
                ...item,
                min_initial_qty: Number(item.min_initial_qty) || 0,
                min_reorder_qty: Number(item.min_reorder_qty) || 0,
                display_qty: Number(item.display_qty) || 0,
                wholesale_cents: Number(item.wholesale_cents) || 0,
                suggested_retail_cents: Number(item.suggested_retail_cents) || 0,
                consignment_repasse_cents: Number(item.consignment_repasse_cents) || 0,
            })),
        };
        try {
            const response = isEdit
                ? await api.put(`/commerce-partners/${uuid}`, payload)
                : await api.post('/commerce-partners', payload);
            if (!response?.data?.success) {
                const errors = response?.data?.errors || {};
                Swal.fire('Atenção', Object.values(errors)[0] || response?.data?.error || 'Verifique os campos.', 'warning');
                return;
            }
            navigate(`/admin/partners/${response.data.data.uuid}`);
        } catch (error) {
            Swal.fire('Erro', error?.response?.data?.error || 'Falha ao salvar.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const isConsignment = isVisitManagedPartnerModality(form.modality);
    const isConsignmentOrRepasse = isVisitManagedPartnerModality(form.modality);

    return (
        <Box component="form" onSubmit={handleSubmit}>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link component={RouterLink} to="/admin/partners" underline="hover" color="inherit">Parcerias</Link>
                <Typography color="text.primary">{isEdit ? 'Editar' : 'Nova'}</Typography>
            </Breadcrumbs>
            <Typography variant="h5" sx={{ mb: 2 }}>{isEdit ? 'Editar parceria' : 'Nova parceria'}</Typography>

            <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Estabelecimento</Typography>
                <Grid container spacing={2.5}>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth required label="Nome do estabelecimento" value={form.name} onChange={(e) => setField('name', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                            <InputLabel>Modalidade</InputLabel>
                            <Select label="Modalidade" value={form.modality} onChange={(e) => setField('modality', e.target.value)}>
                                <MenuItem value="pay_to_sell">Pay to sell</MenuItem>
                                <MenuItem value="consignment">Consignação</MenuItem>
                                <MenuItem value="repasse">Repasse (Comissão)</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select label="Status" value={form.status} onChange={(e) => setField('status', e.target.value)}>
                                <MenuItem value="active">Ativa</MenuItem>
                                <MenuItem value="paused">Pausada</MenuItem>
                                <MenuItem value="ended">Encerrada</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                <Box
                    sx={{
                        mt: 2,
                        px: 2,
                        py: 1.5,
                        borderRadius: 2,
                        bgcolor: 'rgba(2, 136, 209, 0.08)',
                        border: '1px solid',
                        borderColor: 'rgba(2, 136, 209, 0.24)',
                    }}
                >
                    <Typography
                        variant="body2"
                        color="text.primary"
                        component="div"
                        sx={{
                            lineHeight: 1.7,
                            '& strong': {
                                fontWeight: 700,
                                color: 'info.dark',
                            },
                            '& br': {
                                display: 'block',
                                content: '""',
                                mb: 1,
                            },
                        }}
                        dangerouslySetInnerHTML={{
                            __html: modalityDisclaimer[form.modality]
                                || `Configuração da modalidade ${partnerModalityLabel(form.modality).toLowerCase()}.`,
                        }}
                    />
                </Box>
            </Paper>

            <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Contato</Typography>
                <Grid container spacing={2.5}>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            required
                            label="Nome do responsável"
                            value={form.contact_name || ''}
                            onChange={(e) => setField('contact_name', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <PhoneInput
                            required
                            label="Telefone"
                            value={form.contact_phone || ''}
                            onChange={(e) => setField('contact_phone', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField fullWidth label="Email" value={form.contact_email || ''} onChange={(e) => setField('contact_email', e.target.value)} />
                    </Grid>
                </Grid>
            </Paper>

            <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 0.5 }}>Endereço</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Usado para o pin no mapa de visitas.
                </Typography>
                <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            required
                            label="CEP"
                            value={formatCep(form.zip_code)}
                            onChange={(e) => {
                                const cep = normalizeCep(e.target.value);
                                setForm((prev) => ({
                                    ...prev,
                                    zip_code: cep,
                                    ...(cep.length < 8
                                        ? { address: '', neighborhood: '', city: '', state: '' }
                                        : {}),
                                }));
                                setCepError('');
                            }}
                            inputProps={{ maxLength: 9, inputMode: 'numeric' }}
                            error={Boolean(cepError)}
                            helperText={cepError || (isLoadingCep ? 'Buscando endereço...' : ' ')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={8}>
                        <TextField
                            fullWidth
                            required
                            label="Logradouro"
                            value={form.address || ''}
                            onChange={(e) => setField('address', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            required
                            label="Número"
                            value={form.address_number || ''}
                            onChange={(e) => setField('address_number', e.target.value)}
                            inputRef={addressNumberInputRef}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField fullWidth label="Complemento" value={form.address_complement || ''} onChange={(e) => setField('address_complement', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField fullWidth label="Bairro" value={form.neighborhood || ''} onChange={(e) => setField('neighborhood', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={8} md={8}>
                        <TextField fullWidth label="Cidade" value={form.city || ''} onChange={(e) => setField('city', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={4} md={4}>
                        <TextField fullWidth label="UF" value={form.state || ''} onChange={(e) => setField('state', e.target.value)} inputProps={{ maxLength: 2 }} />
                    </Grid>
                </Grid>
            </Paper>

            {isConsignment ? (
                <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 0.5 }}>Agenda de visitas</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        A periodicidade gera o calendário a partir da primeira visita.
                    </Typography>
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Frequência de visita</InputLabel>
                                <Select label="Frequência de visita" value={form.visit_frequency || 'weekly'} onChange={(e) => setField('visit_frequency', e.target.value)}>
                                    <MenuItem value="weekly">Semanal</MenuItem>
                                    <MenuItem value="biweekly">Quinzenal</MenuItem>
                                    <MenuItem value="monthly">Mensal</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth type="date" label="Primeira visita" InputLabelProps={{ shrink: true }} value={form.first_visit_on || ''} onChange={(e) => setField('first_visit_on', e.target.value)} />
                        </Grid>
                    </Grid>
                </Paper>
            ) : null}

            <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Observações</Typography>
                <TextField fullWidth multiline minRows={3} label="Anotações internas" value={form.notes || ''} onChange={(e) => setField('notes', e.target.value)} />
            </Paper>

            <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
                    <Box>
                        <Typography variant="h6">Produtos e acessórios</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {form.modality === 'repasse'
                                ? 'Informe quantidades e o desconto que você paga ao parceiro por unidade.'
                                : isConsignmentOrRepasse
                                    ? 'Informe quantidades e o valor que você recebe por unidade vendida.'
                                    : `Informe os preços da modalidade ${partnerModalityLabel(form.modality).toLowerCase()}.`}
                        </Typography>
                    </Box>
                    <Button variant="outlined" onClick={() => setForm((prev) => ({ ...prev, items: [...prev.items, emptyItem()] }))}>
                        Adicionar produto
                    </Button>
                </Stack>
                {isConsignmentOrRepasse ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {form.modality === 'repasse' ? (
                            <>
                                <strong>Desconto do parceiro:</strong> por unidade. Você cobra o varejo do cliente e paga só este valor.
                            </>
                        ) : (
                            <>
                                <strong>Valor que você recebe:</strong> por unidade. O parceiro cobra o varejo e te paga o total menos o desconto.
                            </>
                        )}
                    </Alert>
                ) : null}
                {form.items.map((item, index) => (
                    <Paper key={index} variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, mb: 2 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="subtitle1">Item {index + 1}</Typography>
                            <IconButton
                                color="error"
                                aria-label="Remover item"
                                onClick={() => setForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }))}
                            >
                                <Delete />
                            </IconButton>
                        </Stack>
                        <Grid container spacing={2.5}>
                            <Grid item xs={12} md={8}>
                                <TextField
                                    fullWidth
                                    label={isLoadingProductSearch && searchingItemIndex === index ? 'Buscando produto...' : 'Produto'}
                                    placeholder="Digite ao menos 3 letras para buscar"
                                    value={item.product_name || ''}
                                    helperText={item.product_uuid ? 'Produto selecionado do catálogo' : 'Busque e escolha um produto da lista'}
                                    onFocus={(e) => {
                                        if (item.product_uuid || suppressProductSearch.current) {
                                            return;
                                        }
                                        if ((item.product_name || '').length >= 3) {
                                            productSearch(item.product_name, index, e.currentTarget);
                                        }
                                    }}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        suppressProductSearch.current = false;
                                        setForm((prev) => {
                                            const items = [...prev.items];
                                            items[index] = { ...items[index], product_name: value, product_uuid: '' };
                                            return { ...prev, items };
                                        });
                                        productSearch(value, index, e.currentTarget);
                                    }}
                                />
                                <Menu
                                    anchorEl={anchorElProduct}
                                    open={Boolean(anchorElProduct) && searchingItemIndex === index && productResults.length > 0}
                                    onClose={closeProductMenu}
                                    disableAutoFocus
                                    disableAutoFocusItem
                                    disableRestoreFocus
                                >
                                    {productResults.map((product) => (
                                        <MenuItem
                                            key={product.uuid || product.id}
                                            onClick={() => selectProduct(index, product)}
                                        >
                                            {product.name} — {formatCurrencyInCents(product.base_price)}
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Tipo</InputLabel>
                                    <Select label="Tipo" value={item.item_kind || 'stock_product'} onChange={(e) => setItem(index, 'item_kind', e.target.value)}>
                                        <MenuItem value="accessory">Acessório</MenuItem>
                                        <MenuItem value="stock_product">Produto</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Quantidade inicial"
                                    value={item.min_initial_qty}
                                    onChange={(e) => setItem(index, 'min_initial_qty', e.target.value)}
                                    helperText={isConsignmentOrRepasse ? 'Vira o estoque inicial no ponto ao salvar o cadastro' : undefined}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField fullWidth type="number" label="Mínimo por pedido" value={item.min_reorder_qty} onChange={(e) => setItem(index, 'min_reorder_qty', e.target.value)} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Qtd. no expositor"
                                    value={item.display_qty}
                                    onChange={(e) => setItem(index, 'display_qty', e.target.value)}
                                    disabled={!isConsignment}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <MoneyInputWithBlur
                                    label="Valor de aquisição no Atacado"
                                    value={centsToReaisInput(item.wholesale_cents)}
                                    onChange={(values) => setItem(index, 'wholesale_cents', parseFormattedMoneyToCents(values?.floatValue, values?.value))}
                                    helperText={isConsignment ? 'Usado no pay to sell' : undefined}
                                    disabled={isConsignment}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <MoneyInputWithBlur
                                    label="Varejo sugerido para venda"
                                    value={centsToReaisInput(item.suggested_retail_cents)}
                                    onChange={(values) => setItem(index, 'suggested_retail_cents', parseFormattedMoneyToCents(values?.floatValue, values?.value))}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <MoneyInputWithBlur
                                    label={form.modality === 'repasse' ? 'Desconto do parceiro' : 'Valor que eu recebo'}
                                    value={centsToReaisInput(item.consignment_repasse_cents)}
                                    onChange={(values) => setItem(index, 'consignment_repasse_cents', parseFormattedMoneyToCents(values?.floatValue, values?.value))}
                                    helperText={
                                        form.modality === 'repasse'
                                            ? 'Por unidade. Você cobra o varejo e paga só este valor.'
                                            : form.modality === 'consignment'
                                                ? 'Por unidade. O parceiro cobra o varejo e te paga o restante.'
                                                : 'Usado na Consignação/Repasse'
                                    }
                                    disabled={!isConsignmentOrRepasse}
                                />
                            </Grid>
                            {isConsignmentOrRepasse ? (
                                <Grid item xs={12}>
                                    <RepasseExampleBox
                                        retailCents={item.suggested_retail_cents}
                                        repasseCents={item.consignment_repasse_cents}
                                        modality={form.modality}
                                    />
                                </Grid>
                            ) : null}
                        </Grid>
                    </Paper>
                ))}
            </Paper>

            <Stack direction="row" spacing={1.5} sx={{ mb: 4 }}>
                <Button type="submit" variant="contained" size="large" disabled={saving}>{saving ? 'Salvando…' : 'Salvar'}</Button>
                <Button component={RouterLink} to={isEdit ? `/admin/partners/${uuid}` : '/admin/partners/list'} size="large">Cancelar</Button>
            </Stack>
        </Box>
    );
}

export default PartnerFormPage;
